import { chain, get, uniq } from 'lodash';

import type {
  AddItemPurchaseOrderReq,
  GetPurchaseOrderDetailsResponse,
  GroupUserAdvancements,
  ItemsRow,
} from '@appTypes/esb';
import { OrgActivitiesDashboard } from '@appTypes/esb/organizations/v2/_orgGuid/unitActivitiesDashboard';
import { getArnicaRootUrl } from '@config';
import {
  ProgramId,
  UnapproveTypes,
  advancementTypes,
  advancementTypesName,
  ranksIds,
  unapproveAdvancementTypes,
  unitTypeForProgramName,
  unitTypes,
} from '@modules/shared/constants';
import { getPreferredName } from '@modules/shared/utils/preferredName';

import { DenormalizedHistoryItem } from '../advancementHistory/utils';
import { pocketCertificateTypes } from '../constants';
import type { RosterScout } from '../duck/types';
import { PendingItemsTabs } from '../pendingItems/constants';
import { filterItems } from '../unitAdvancement/duck/utilsTyped';
import type { ItemsPerSubUnit, ToApproveItems } from '../unitAdvancement/types';
import { arnicaAPIPdfRankValues } from './constants';
import type {
  DeleteItemPurchaseOrderReq,
  FilterPODetails,
  GroupByTypeUserAdvancements,
  GroupByYouthUserAdvancements,
  POItemWithSubUnitDetails,
  selectedAdvancementsPendingItem,
} from './duck/types';

type getArnicaPDFParams = {
  api: string;
  poid: number;
  unitId: number;
  rankId?: number;
  isDateEarned?: number;
  isEsignature?: number;
  sku?: number;
  filterPODetails?: FilterPODetails;
};

export const getArnicaPDFUrl = (api: string, params: getArnicaPDFParams) => {
  const {
    poid,
    unitId,
    rankId,
    isDateEarned = 0,
    isEsignature = 0,
    sku,
    filterPODetails,
  } = params;
  const searchParams = new URLSearchParams();
  const rootURL = getArnicaRootUrl();

  let finalURL = `${rootURL}`;

  searchParams.append('Method', 'ExecuteContainerAPI');
  searchParams.append('Container', 'SB_PO_API');
  searchParams.append('API', api);

  searchParams.append('POID', poid.toString());
  searchParams.append('UnitID', unitId.toString());

  if (rankId) {
    searchParams.append('Rank', rankId.toString());
  }

  if (sku) {
    searchParams.append('sku', sku.toString());
  }

  if (filterPODetails) {
    let show = '';

    if (filterPODetails.sku) show += '@SKU';
    if (filterPODetails.prices) show += '@Price';
    if (filterPODetails.notes) show += '@Notes';

    searchParams.append('Show', show);

    if (!filterPODetails.noGrouping) {
      let groupBy = '';

      if (filterPODetails.youth) {
        groupBy += '@SKU';
      } else {
        groupBy += '@ItemType';
      }

      if (filterPODetails.subUnit) groupBy += '@SubUnit';

      searchParams.append('GroupBy', groupBy);
    }
  }

  searchParams.append('IsDateEarnedIncluded', isDateEarned.toString());
  searchParams.append('IsESignatureIncluded', isEsignature.toString());

  return (finalURL += `?${params.toString()}`);
};

export const listOfSKU = {
  ADVENTURE: 646960,
  ADVENTURE_2: 661565,
  LION: 644815,
  LION_2: 661561,
  TIGER: 620807,
  WOLF: 621021,
  BEAR: 621023,
  BEAR_2: 646954,
  WEBELOS: 621025,
  WEBELOS_2: 645219,
  ARROW: 621027,
  ARROW_2: 661564,
  MERIT_BADGE: 654936,
};

export const getSKU = (type: string) => {
  switch (type) {
    case pocketCertificateTypes.ADVENTURE:
      return {
        first: listOfSKU.ADVENTURE,
        second: listOfSKU.ADVENTURE_2,
      };
    case pocketCertificateTypes.LION:
      return {
        first: listOfSKU.LION,
        second: listOfSKU.LION_2,
      };
    case pocketCertificateTypes.TIGER:
      return {
        first: listOfSKU.TIGER,
        second: null,
      };
    case pocketCertificateTypes.WOLF:
      return {
        first: listOfSKU.WOLF,
        second: null,
      };
    case pocketCertificateTypes.BEAR:
      return {
        first: listOfSKU.BEAR,
        second: listOfSKU.BEAR_2,
      };
    case pocketCertificateTypes.WEBELOS:
      return {
        first: listOfSKU.WEBELOS,
        second: listOfSKU.WEBELOS_2,
      };
    case pocketCertificateTypes.ARROW:
      return {
        first: listOfSKU.ARROW,
        second: listOfSKU.ARROW_2,
      };
    case pocketCertificateTypes.MERIT_BADGE:
      return {
        first: listOfSKU.MERIT_BADGE,
        second: null,
      };
    default:
      break;
  }
};

export const getAPIRankValue = (type: string) => {
  switch (type) {
    case pocketCertificateTypes.TIGER:
      return arnicaAPIPdfRankValues.TIGER;
    case pocketCertificateTypes.WOLF:
      return arnicaAPIPdfRankValues.WOLF;
    case pocketCertificateTypes.BEAR:
      return arnicaAPIPdfRankValues.BEAR;
    case pocketCertificateTypes.LION:
      return arnicaAPIPdfRankValues.LION;
    case pocketCertificateTypes.WEBELOS:
      return arnicaAPIPdfRankValues.WEBELOS;
    case pocketCertificateTypes.ARROW:
      return arnicaAPIPdfRankValues.ARROW;
    default:
      return 0;
  }
};

export const getImgSrc = (sku: number) => {
  const url = `https://d3hdyt7ugiz6do.cloudfront.net/images/certificates/${sku}.png`;
  return url;
};

export const createPayloadAddItemPO = (
  organizationGuid: string,
  purchaseOrderId: number,
  selectedAdvancements: selectedAdvancementsPendingItem[],
): {
  organizationGuid: string;
  purchaseOrderId: number;
  body: AddItemPurchaseOrderReq[];
} => {
  const body = selectedAdvancements.map(adv => {
    const approachVersionId = [
      advancementTypes.RANKS,
      advancementTypes.MERIT_BADGES,
      advancementTypes.ADVENTURES,
    ];

    if (approachVersionId.includes(adv.advancementType)) {
      return {
        userId: adv.userId,
        advancementType: adv.advancementType,
        advancementVersionId: adv.versionId,
      };
    } else {
      return {
        userId: adv.userId,
        advancementType: adv.advancementType,
        userAwardId: adv.userAwardId,
      };
    }
  });

  return {
    organizationGuid: organizationGuid,
    purchaseOrderId: purchaseOrderId,
    body: body,
  };
};

export const createPayloadDeleteItemPO = (
  organizationGuid: string,
  purchaseOrderId: number,
  itemsRow: ItemsRow[],
): {
  organizationGuid: string;
  purchaseOrderId: number;
  body: DeleteItemPurchaseOrderReq[];
} => {
  let body: DeleteItemPurchaseOrderReq[] = [];

  itemsRow.forEach(adv => {
    const { advancementType, advancementId, members } = adv;
    members.forEach(member => {
      const formattedItems: DeleteItemPurchaseOrderReq = {
        userId: member.userId,
        advancementType,
        advancementId,
      };
      //include userAdvancementId only for Awards
      if (advancementType === advancementTypes.AWARDS) {
        formattedItems.userAwardId = member.userAdvancementId;
      } else {
        formattedItems.advancementVersionId = member.advancementVersionId;
      }
      body = body.concat(formattedItems);
    });
  });

  return {
    organizationGuid: organizationGuid,
    purchaseOrderId: purchaseOrderId,
    body,
  };
};

export const createPayloadForSingleDeleteItemPO = (
  organizationGuid: string,
  purchaseOrderId: number,
  data: {
    youthUserId: number;
    advancementType: string;
    userAwardId: number;
    id: number;
    activeVersionId: number;
  },
): {
  organizationGuid: string;
  purchaseOrderId: number;
  body: DeleteItemPurchaseOrderReq[];
} => {
  let body: DeleteItemPurchaseOrderReq[] = [];

  const { youthUserId, advancementType, id, userAwardId, activeVersionId } =
    data;

  const formattedItems: DeleteItemPurchaseOrderReq = {
    userId: youthUserId,
    advancementType,
    advancementId: id,
  };

  if (advancementType === advancementTypes.AWARDS) {
    formattedItems.userAwardId = userAwardId;
  } else {
    formattedItems.advancementVersionId = activeVersionId;
  }
  body = body.concat(formattedItems);

  return {
    organizationGuid: organizationGuid,
    purchaseOrderId: purchaseOrderId,
    body,
  };
};

export const orderItemsGrouped = (
  order: GetPurchaseOrderDetailsResponse,
): GroupUserAdvancements[] => {
  const { userAdvancements = [] } = order;
  const combinedItems = userAdvancements.reduce(
    (acc: GroupUserAdvancements[], item, currentIndex: number) => {
      const {
        userId,
        advancementType,
        id,
        userAwardId: userAdvancementId,
        versionId: advancementVersionId,
      } = item;
      const member = {
        name: getPreferredName(item),
        userId,
        advancementId: id,
        userAdvancementId,
        advancementVersionId,
      };
      // Check if SKU already exists in accumulator
      const existingItem = acc.find(i => i.sku === item.sku);
      if (existingItem !== undefined) {
        // SKU already exists, just update the members and totalPrice
        if (existingItem.members) existingItem.members.push(member);
        if (existingItem.totalPrice) existingItem.totalPrice += item.price;
        if (existingItem.quantity) existingItem.quantity++;
      } else {
        // SKU doesn't exist, create a new entry
        acc.push({
          name: item.name,
          sku: item.sku,
          members: [member],
          unitPrice: item.price,
          totalPrice: item.price,
          quantity: 1,
          advancementType,
          advancementId: id,
          key: currentIndex,
        });
      }
      return acc;
    },
    [],
  );

  return combinedItems;
};

export const getNotificationCountActivity = (
  unitActivitiesCount: OrgActivitiesDashboard,
) =>
  Object.values(unitActivitiesCount).reduce((acc, item) => {
    const property = Object.keys(item).find(key =>
      key.toLocaleLowerCase().includes('scoutparticipating'),
    );
    if (property) {
      return acc + item[property];
    }
    return acc + 0;
  }, 0);

export const getNotificationCount = (
  advancementTab: string,
  counts: {
    totalCounts: number;
    toApproveCount: number;
    toPurchaseCount: number;
    ranksCount: number;
    adventuresCount: number;
    meritBadgesCount: number;
    awardsCount: number;
    requirementsCount: number;
  },
) => {
  switch (advancementTab) {
    case PendingItemsTabs.RANKS:
      return counts.ranksCount;
    case PendingItemsTabs.ADVENTURES:
      return counts.adventuresCount;
    case PendingItemsTabs.MERIT_BADGES:
      return counts.meritBadgesCount;
    case PendingItemsTabs.AWARDS:
      return counts.awardsCount;
    case PendingItemsTabs.REQUIREMENTS:
      return counts.requirementsCount;
    default:
      return 0;
  }
};

export const filterRanksPackOrTroop = (
  items: DenormalizedHistoryItem[],
  unitTypeId: number,
) => {
  const isPack = ProgramId.CUB_SCOUT === unitTypeId;
  const troopAdvIds: number[] = [
    ranksIds.SCOUT,
    ranksIds.TENDERFOOT,
    ranksIds.SECOND_CLASS,
    ranksIds.FIRST_CLASS,
    ranksIds.STAR_SCOUT,
    ranksIds.LIFE_SCOUT,
    ranksIds.EAGLE_SCOUT,
  ];
  const packAdvIds: number[] = [
    ranksIds.TIGER,
    ranksIds.WOLF,
    ranksIds.BEAR,
    ranksIds.WEBELOS,
    ranksIds.ARROW_OF_LIGHT,
    ranksIds.BOBCAT,
    ranksIds.LION,
  ];
  const filteredRanks = isPack
    ? items.filter(adv => packAdvIds.includes(adv.id))
    : items.filter(adv => troopAdvIds.includes(adv.id));
  const excludedRanks = items.filter(
    adv => adv.advancementType !== advancementTypes.RANKS,
  );
  const records = excludedRanks.concat(filteredRanks);

  return records;
};

export const isArrayGroupBySubUnit = (items: ToApproveItems[]) =>
  items.some(item => (item as ItemsPerSubUnit).isHeader);

export const searchOnItems = (searchQuery: string, items: ToApproveItems[]) => {
  if (isArrayGroupBySubUnit(items)) {
    return (items as ItemsPerSubUnit[]).map(sub => ({
      ...sub,
      children: filterItems(sub.children, searchQuery),
    }));
  }

  if (searchQuery) {
    return filterItems(items, searchQuery);
  }

  return items;
};

export const achieveFirstClassAndAbove = (
  memberId: number,
  items: RosterScout[],
) => {
  const firstClassAboveRankIds: number[] = [
    ranksIds.FIRST_CLASS,
    ranksIds.STAR_SCOUT,
    ranksIds.LIFE_SCOUT,
    ranksIds.EAGLE_SCOUT,
  ];

  const personFound = items.find(person => person.memberId === memberId);

  const hasFirstClassOrHigher =
    personFound?.highestRanksApproved?.some(
      rank => rank.id !== undefined && firstClassAboveRankIds.includes(rank.id),
    ) ||
    personFound?.highestRanksAwarded?.some(
      rank => rank.id !== undefined && firstClassAboveRankIds.includes(rank.id),
    );

  return hasFirstClassOrHigher;
};

export const getProgramNameFromUnitLong = (unitLong: string) => {
  const index = unitLong.indexOf(' ');
  const unitType =
    index === -1
      ? unitLong
      : (unitLong.slice(0, index) as keyof typeof unitTypes);

  return get(unitTypeForProgramName, unitType);
};

export const implementGroupBy = (
  items: GroupUserAdvancements[],
  filterPODetails: FilterPODetails,
):
  | GroupByTypeUserAdvancements[]
  | GroupUserAdvancements[]
  | GroupByYouthUserAdvancements[] => {
  if (filterPODetails.itemType) {
    const uniqAdvancementType = uniq(items.map(item => item.advancementType));

    const groupedByType = uniqAdvancementType.map(type => {
      const advType = unapproveAdvancementTypes[
        type as UnapproveTypes
      ] as advancementTypesName;

      return {
        key: advType,
        advancementType: advType,
        children: items
          .filter(item => item.advancementType === type)
          .map(item => ({
            ...item,
            advancementType: advType,
          })),
      };
    });

    return groupedByType;
  }

  if (filterPODetails.youth) {
    const attendeesMap = new Map<string, GroupByYouthUserAdvancements>();

    items.forEach(item => {
      const { members } = item;

      if (!Array.isArray(members)) return;

      members.forEach(({ name: youthName, userId }) => {
        if (!attendeesMap.has(youthName)) {
          attendeesMap.set(youthName, {
            key: `${userId}`,
            youthName: youthName,
            userId: userId,
            children: [],
          });
        }
        attendeesMap.get(youthName)!.children.push({
          ...item,
        });
      });
    });

    return Array.from(attendeesMap.values());
  }

  return items;
};

export const implementGroupBySub = (
  allSubUnit: ItemsPerSubUnit[],
  advancementsWithMembers: GroupUserAdvancements[],
) => {
  const updatedList = allSubUnit
    .map(subUnit => {
      const children = advancementsWithMembers
        .map(adv => {
          const { members } = adv;

          if (!Array.isArray(members)) return;

          const matchingItems = members.filter(advMember =>
            subUnit.subRows.some(member => member.userId === advMember.userId),
          );

          return matchingItems.length > 0
            ? { ...adv, members: matchingItems }
            : null;
        })
        .filter(Boolean);

      return { ...subUnit, children };
    })
    .filter(sub => sub.children.length > 0);

  return updatedList;
};

const GroupByType = (formattedList: POItemWithSubUnitDetails[]) => {
  const groupList = formattedList.reduce((acc, youth) => {
    const { advancementType = '' } = youth;

    let advancementEntry = acc.find(
      item => item.advancementType === advancementType,
    );

    if (!advancementEntry) {
      advancementEntry = { advancementType, children: [] };
      acc.push(advancementEntry);
    }

    advancementEntry.children.push({ ...youth });

    return acc;
  }, [] as { advancementType: string; children: { youthName: string }[] }[]);

  return groupList;
};

const GroupByYouth = (formattedList: POItemWithSubUnitDetails[]) => {
  const groupList = formattedList.reduce((acc, youth) => {
    const { userId, youthName } = youth;

    let advancementEntry = acc.find(item => item.userId === userId);

    if (!advancementEntry) {
      advancementEntry = { userId, youthName, children: [] };
      acc.push(advancementEntry);
    }

    advancementEntry.children.push({ ...youth });

    return acc;
  }, [] as GroupByYouthUserAdvancements[]);

  return groupList;
};

const GroupBySubUnitOnly = (formattedList: POItemWithSubUnitDetails[]) => {
  const reconstructFormat = chain(formattedList)
    .groupBy(sub => `${sub.subUnitName}__${sub.subUnitId}`)
    .map((children, key) => {
      const [subUnitName, subUnitId] = key.split('__');
      return {
        isHeader: true,
        key: subUnitId,
        subUnitName,
        subUnitId: Number(subUnitId),
        children,
      };
    })
    .value();

  return reconstructFormat;
};

const GroupBySubUnitAndType = (formattedList: POItemWithSubUnitDetails[]) => {
  const groupList = formattedList.reduce((acc, youth) => {
    const { subUnitName = '', advancementType } = youth;

    let subUnitList = acc.find(s => s.subUnitName === subUnitName);
    if (!subUnitList) {
      subUnitList = { subUnitName, children: [] };
      acc.push(subUnitList);
    }

    let advList = subUnitList.children.find(
      t => t.advancementType === advancementType,
    );
    if (!advList) {
      advList = { advancementType, children: [] };
      subUnitList.children.push(advList);
    }

    advList.children.push({ ...youth });

    return acc;
  }, [] as { subUnitName: string; children: { advancementType: string; children: { userId: number; youthName: string }[] }[] }[]);

  return groupList;
};

const GroupBySubUnitAndYouth = (formattedList: POItemWithSubUnitDetails[]) => {
  const groupList = formattedList.reduce((acc, youth) => {
    const { subUnitName = '', youthName } = youth;

    let subUnitList = acc.find(s => s.subUnitName === subUnitName);
    if (!subUnitList) {
      subUnitList = { subUnitName, children: [] };
      acc.push(subUnitList);
    }

    let youthList = subUnitList.children.find(t => t.youthName === youthName);
    if (!youthList) {
      youthList = { youthName, children: [] };
      subUnitList.children.push(youthList);
    }

    youthList.children.push({ ...youth });

    return acc;
  }, [] as { subUnitName: string; children: { youthName: string; children: { userId: number; youthName: string }[] }[] }[]);

  return groupList;
};

const formatPOItemWithSubUnitDetails = (
  order: GetPurchaseOrderDetailsResponse,
  subUnitWithYouth: ItemsPerSubUnit[],
) => {
  const { userAdvancements = [] } = order;

  const youthWtihSubUnit = subUnitWithYouth.flatMap(sub =>
    sub.subRows.map(youth => ({
      ...youth,
      key: sub.subUnitId,
      subUnitId: sub.subUnitId,
      subUnitName: sub.subUnitName,
      subUnitNameRaw: sub.subUnitNameRaw,
    })),
  );

  const formattedList = userAdvancements.map(user => {
    const subUnitDetails = youthWtihSubUnit.find(
      youth => youth.userId === user.userId,
    );

    return {
      name: user.name,
      sku: user.sku,
      unitPrice: user.price,
      totalPrice: user.price,
      quantity: 1,
      advancementType: user.advancementType,
      advancementId: user.id,
      key: user.userId,
      advancementVersionId: user.versionId,
      youthName: getPreferredName(user),
      userAdvancementId: user.userAwardId,
      userId: user.userId,
      subUnitId: subUnitDetails?.subUnitId,
      subUnitName: subUnitDetails?.subUnitName || 'unassigned',
      subUnitNameRaw: subUnitDetails?.subUnitNameRaw || 'unassigned',
    };
  });

  return formattedList;
};

export const performPOGroupBy = (
  order: GetPurchaseOrderDetailsResponse,
  subUnitWithYouth: ItemsPerSubUnit[],
  filterPODetails: FilterPODetails,
) => {
  const formattedList = formatPOItemWithSubUnitDetails(order, subUnitWithYouth);

  if (filterPODetails.subUnit) {
    if (filterPODetails.itemType) {
      const groupBySubUnitAndType = GroupBySubUnitAndType(formattedList);
      return groupBySubUnitAndType;
    }

    if (filterPODetails.youth) {
      const groupBySubUnitAndYouth = GroupBySubUnitAndYouth(formattedList);
      return groupBySubUnitAndYouth;
    }

    const groupBySubUnit = GroupBySubUnitOnly(formattedList);
    return groupBySubUnit;
  }

  if (filterPODetails.itemType) {
    const groupByType = GroupByType(formattedList);
    return groupByType;
  }

  if (filterPODetails.youth) {
    const groupByYouth = GroupByYouth(formattedList);
    return groupByYouth;
  }

  return formattedList;
};
